import React from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs, 
  Link, 
  Divider, 
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import FooterSection from "../home/FooterSection";

// Custom bullet point component
const BulletPoint = () => (
  <Box 
    component="span" 
    sx={{ 
      display: "inline-block",
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: "#0f84fe",
      marginRight: 1.5,
      flexShrink: 0,
      mt: 0.8
    }} 
  />
);

const PrivacyPolicy = () => {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* <NavigationSection /> */}
      
      {/* Header Banner */}
      <Box
        sx={{
          bgcolor: "#232323",
          color: "white",
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 }
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 600,
              mb: 2
            }}
          >
            Privacy Policy
          </Typography>
          
          <Breadcrumbs 
            aria-label="breadcrumb" 
            sx={{ 
              color: "#8d8d8b",
              "& .MuiBreadcrumbs-separator": {
                mx: 1
              }
            }}
          >
            <Link 
              underline="hover" 
              color="inherit" 
              href="/"
              sx={{ 
                "&:hover": { color: "#0f84fe" },
                transition: "color 0.2s"
              }}
            >
              Home
            </Link>
            <Typography color="#8d8d8b">Privacy Policy</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 },
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            borderRadius: 2
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#0f84fe",
              fontWeight: 600,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 1
            }}
          >
            Last Updated: May 19, 2025
          </Typography>
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Introduction
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            Alfa 2020 s.r.o. ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
            explains how we collect, use, disclose, and safeguard your information when you use our remote technical 
            support, software installation, network setup and troubleshooting services, including when you visit our 
            website or engage with our support technicians.
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            Please read this Privacy Policy carefully. By accessing our services, you acknowledge the practices described 
            in this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Information We Collect
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            In order to provide our technical support services effectively, we may collect various types of information:
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Personal Information
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Contact information (name, email address, phone number, mailing address)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Account credentials (username, password)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Payment information (credit card details, billing address)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Service history (details of previous technical support sessions)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Technical Information
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Device information (hardware model, operating system version, system configuration)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Network information (IP address, MAC address, network configuration details)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Software information (installed applications, version numbers, configuration settings)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Diagnostic information (error logs, crash reports, system performance data)" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Website Usage Information
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Browser type and version" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Time spent on our website and pages visited" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Referral source" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Cookies and similar tracking technologies" 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            How We Use Your Information
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            We use the information we collect for various purposes, including:
          </Typography>
          
          <List sx={{ pl: 2, mb: 4 }}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Providing remote technical support, software installation, and network troubleshooting services" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Processing payments and managing your account" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Resolving technical issues and diagnosing problems" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Communicating with you about our services, updates, and promotions" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Improving our services and developing new features" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Analyzing usage patterns to enhance user experience" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Complying with legal obligations" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Information Sharing and Disclosure
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            We may share your information in the following circumstances:
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Service Providers
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              We may share your information with third-party service providers who perform services on our behalf, 
              such as payment processing, data analysis, email delivery, hosting services, and customer service. 
              These service providers are contractually obligated to protect your information and are restricted 
              from using the data for any purpose other than providing services to us.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Business Transfers
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information 
              may be transferred as part of that transaction. We will notify you via email and/or a prominent notice 
              on our website of any change in ownership or uses of your personal information.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Legal Compliance
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              We may disclose your information where required to do so by law or subpoena or if we believe that such action 
              is necessary to comply with the law and the reasonable requests of law enforcement or to protect the security 
              or integrity of our services.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              With Your Consent
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              We may share your information with your consent or at your direction.
            </Typography>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Data Security
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            We implement appropriate technical and organizational security measures designed to protect the security of 
            any personal information we process. However, despite our safeguards and efforts to secure your information, 
            no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% 
            secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties 
            will not be able to defeat our security and improperly collect, access, steal, or modify your information.
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            When providing remote technical support services, our technicians adhere to strict security protocols to 
            protect your data and system integrity. All remote access sessions are encrypted, and we maintain detailed 
            logs of all support activities performed.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Your Rights and Choices
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            Depending on your location, you may have certain rights regarding your personal information:
          </Typography>
          
          <List sx={{ pl: 2, mb: 4 }}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Access, update, or delete your personal information" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Object to or restrict processing of your personal information" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Request portability of your personal information" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Opt-out of marketing communications" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            To exercise these rights, please contact us at info@alfagroup-24.com. We will respond to your request 
            within a reasonable timeframe.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Third-Party Links
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            Our website may contain links to third-party websites and services. We have no control over and assume 
            no responsibility for the content, privacy policies, or practices of any third-party sites or services. 
            We encourage you to review the privacy policies of any third-party sites you visit.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Children's Privacy
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            Our services are not intended for individuals under the age of 16. We do not knowingly collect personal 
            information from children under 16. If we become aware that we have collected personal information from 
            a child under 16 without verification of parental consent, we will take steps to remove that information 
            from our servers.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Changes to This Privacy Policy
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new 
            Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy. You 
            are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy 
            are effective when they are posted on this page.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Contact Us
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 2, lineHeight: 1.7 }}>
            If you have any questions about this Privacy Policy, please contact us:
          </Typography>
          
          <Box sx={{ ml: 2, mb: 4 }}>
            <Typography paragraph sx={{ color: "#444", mb: 1, lineHeight: 1.7 }}>
              <strong>By email:</strong> info@alfagroup-24.com
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              Alfa 2020 s.r.o.<br />
              Veľká Okružná 17 
              Žilina 010 01
            </Typography>
          </Box>
        </Paper>
      </Container>
      
      <FooterSection />
    </Box>
  );
};

export default PrivacyPolicy;